import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { FileText, Building2, Pencil, Search, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { DepotFormTemplateDialog } from './planning/DepotFormTemplateDialog';

/**
 * Ayarlar → Depo Form Şablonları (Faz 4.5 — Şub 03, 2026)
 * --------------------------------------------------------------
 * Tüm planning_depots için form alanlarını TOPLU yönetim:
 *  - Şablonu olan / olmayan depolar tek görünümde
 *  - Tıklayınca DepotFormTemplateDialog açılır
 *  - Şirkete göre gruplandı, son güncelleme bilgileri görünür
 */
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// Filter options for depot template list
const FILTER_OPTIONS = [
  { key: 'all', label: 'Tümü' },
  { key: 'with', label: 'Şablonu Olan' },
  { key: 'without', label: 'Şablonsuz' },
  { key: 'company', label: 'Şirket Şablonu' },
];

export default function DepotFormTemplatesSettings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [editingDepot, setEditingDepot] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  // Haz 14, 2026 — "Varsayılan" butonu için flag. Dialog'a `withDefaults`
  // prop'u olarak iletilir; KM ×2 + Saat ×2 alanlarını eksikse otomatik ekler.
  const [openWithDefaults, setOpenWithDefaults] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/planning/depots/form-templates/summary');
      setRows(res.data?.data || []);
    } catch {
      toast.error('Form şablonu listesi yüklenemedi');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const s = search.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (s && !`${r.depot_name || ''} ${r.company_name || ''}`.toLowerCase().includes(s)) return false;
    if (filterMode === 'with' && !(r.field_count > 0)) return false;
    if (filterMode === 'without' && r.field_count > 0) return false;
    if (filterMode === 'company' && !r.is_company_template) return false;
    return true;
  });

  const grouped = new Map();
  for (const r of filtered) {
    const k = r.company_id || '__none__';
    if (!grouped.has(k)) grouped.set(k, { company_id: k, company_name: r.company_name || '—', depots: [] });
    grouped.get(k).depots.push(r);
  }
  const groups = [...grouped.values()].sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '', 'tr'));

  const totalDepots = rows.length;
  const withTemplate = rows.filter((r) => r.field_count > 0).length;
  const withoutTemplate = totalDepots - withTemplate;

  return (
    <div className="space-y-4" data-testid="depot-form-templates-settings">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <FileText className="h-6 w-6 text-blue-500" /> Depo Form Şablonları
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Her depo için çalışanın günlük doldurması gereken form alanlarını burada yönetin.
          Şablonu olmayan depolarda atama yapılınca form istenmez.
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs uppercase text-slate-500 font-semibold">Toplam Depo</p>
          <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{totalDepots}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs uppercase text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Şablonu Olan</p>
          <p className="text-2xl font-bold text-emerald-600">{withTemplate}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs uppercase text-slate-500 font-semibold flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Şablonsuz</p>
          <p className="text-2xl font-bold text-slate-400">{withoutTemplate}</p>
        </CardContent></Card>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Depo veya şirket ara…"
            className="h-9 text-sm pl-7"
            data-testid="depot-form-tpl-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="depot-form-tpl-filters">
          {FILTER_OPTIONS.map((opt) => {
            const count = opt.key === 'all'
              ? totalDepots
              : opt.key === 'with'
              ? withTemplate
              : opt.key === 'without'
              ? withoutTemplate
              : rows.filter((r) => r.is_company_template).length;
            return (
              <Button
                key={opt.key}
                size="sm"
                variant={filterMode === opt.key ? 'default' : 'outline'}
                className="h-8 px-3 text-xs gap-1"
                onClick={() => setFilterMode(opt.key)}
                data-testid={`depot-form-tpl-filter-${opt.key}`}
              >
                {opt.label}
                <Badge variant="secondary" className="ml-0.5 text-[10px] h-4 px-1.5">{count}</Badge>
              </Button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
        </div>
      ) : groups.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-400" data-testid="depot-form-tpl-empty">
          {search || filterMode !== 'all' ? 'Filtre kriterlerine uygun depo bulunamadı.' : 'Depo bulunamadı.'}
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <Card key={g.company_id} data-testid={`form-tpl-group-${g.company_id}`}>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-500" /> {g.company_name}
                  <Badge variant="secondary" className="ml-1 text-[10px]">{g.depots.length} depo</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 pb-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-700">
                    <tr>
                      <th className="text-left py-2 px-2">Depo</th>
                      <th className="text-center py-2 px-2 w-32">Alan Sayısı</th>
                      <th className="text-center py-2 px-2 w-32">Zorunlu</th>
                      <th className="text-left py-2 px-2 w-44">Son Güncelleme</th>
                      <th className="text-right py-2 px-2 w-28">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.depots.map((d) => (
                      <tr key={d.depot_id || `company-${d.company_id}`} className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 ${d.is_company_template ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''}`} data-testid={`form-tpl-row-${d.depot_id || 'company'}`}>
                        <td className="py-2 px-2 font-medium text-slate-800 dark:text-slate-100">
                          {d.depot_name}
                          {d.start_stop && (
                            <Badge className={`ml-2 text-[10px] ${d.start_stop === 'required' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`} data-testid={`tpl-start-stop-badge-${d.depot_id || d.company_id}`}>
                              Start-Stop{d.start_stop === 'required' ? ' · zorunlu' : ''}
                            </Badge>
                          )}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {d.field_count > 0 ? (
                            <Badge className="bg-blue-100 text-blue-700 border-blue-200">{d.field_count}</Badge>
                          ) : (
                            <span className="text-[11px] text-slate-400">Şablonsuz</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-center text-xs text-slate-500">{d.required_count || '—'}</td>
                        <td className="py-2 px-2 text-xs text-slate-500">
                          {fmtDate(d.updated_at)}
                          {d.updated_by_name && <span className="block text-[10px] opacity-70">{d.updated_by_name}</span>}
                        </td>
                        <td className="py-2 px-2 text-right">
                          <div className="flex justify-end items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                              onClick={() => {
                                // Sadece depo modunda (şirket geneli'nde rota
                                // listesi yok, default route'a uygulanamaz).
                                if (d.is_company_template) {
                                  toast.info('Şirket şablonu için "Düzenle"den varsayılan ekleyin');
                                  setEditingCompany({ id: d.company_id, name: d.company_name });
                                  setOpenWithDefaults(true);
                                  return;
                                }
                                setEditingDepot({ id: d.depot_id, name: d.depot_name });
                                setOpenWithDefaults(true);
                              }}
                              title="Başlangıç/Bitiş KM & Saat alanlarıyla aç"
                              data-testid={`form-tpl-defaults-${d.depot_id || 'company-' + d.company_id}`}
                            >
                              ✨ Varsayılan
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1"
                              onClick={() => {
                                setOpenWithDefaults(false);
                                if (d.is_company_template) {
                                  setEditingCompany({ id: d.company_id, name: d.company_name });
                                } else {
                                  setEditingDepot({ id: d.depot_id, name: d.depot_name });
                                }
                              }}
                              data-testid={`form-tpl-edit-${d.depot_id || 'company-' + d.company_id}`}
                            >
                              <Pencil className="h-3 w-3" /> Düzenle
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DepotFormTemplateDialog
        open={!!editingDepot}
        depot={editingDepot}
        withDefaults={openWithDefaults}
        onClose={() => { setEditingDepot(null); setOpenWithDefaults(false); }}
        onSaved={() => { setEditingDepot(null); setOpenWithDefaults(false); reload(); }}
      />
      <DepotFormTemplateDialog
        open={!!editingCompany}
        company={editingCompany}
        withDefaults={openWithDefaults}
        onClose={() => { setEditingCompany(null); setOpenWithDefaults(false); }}
        onSaved={() => { setEditingCompany(null); setOpenWithDefaults(false); reload(); }}
      />
    </div>
  );
}
